import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicLayout from "@/components/PublicLayout";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { buildHeroStyles } from "@/lib/siteDefaults";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Sparkles, CalendarCheck, Layers, ArrowRight } from "lucide-react";

const FEATURE_ICONS = [Sparkles, CalendarCheck, ShieldCheck];

export default function HomePage() {
  const { user } = useAuth();
  const { content, resolveImg, loaded } = useSiteContent();
  const c = content.home;
  const portalHref = user ? (user.role === "client" ? "/dashboard" : "/admin") : "/login";

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden hero-petrol">
        {!loaded ? null : c.hero_video ? (
          <video data-testid="hero-video" src={resolveImg(c.hero_video)} autoPlay muted loop playsInline preload="auto" aria-hidden
            className="pointer-events-none absolute inset-0 w-full h-full object-cover" />
        ) : (
          <>
            <div aria-hidden className="pointer-events-none hero-blob hero-blob-1" />
            <div aria-hidden className="pointer-events-none hero-blob hero-blob-2" />
            <div aria-hidden className="pointer-events-none hero-blob hero-blob-3" />
          </>
        )}
        <div aria-hidden className="pointer-events-none absolute inset-0 hero-grain" />
        {loaded && c.hero_watermark !== false && (
        <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <img src={resolveImg(content.general.logo)} alt="" className="w-[70%] max-w-3xl opacity-[0.07] object-contain brightness-0 invert" />
        </div>
        )}
        {loaded && c.hero_image && (() => {
          const hs = buildHeroStyles(c.hero_settings);
          return (
          <motion.div
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.95 }}
            transition={{ duration: 1.2 }}
            className="pointer-events-none hidden lg:block absolute -translate-y-1/2 overflow-hidden select-none"
            style={hs.outer}
          >
            <div className="w-full h-full" style={hs.mask}>
              <img src={resolveImg(c.hero_image)} alt="" className="w-full h-full object-cover mix-blend-screen" style={hs.img} />
            </div>
          </motion.div>
          );
        })()}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl text-white pt-20">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-teal-300 mb-5">{c.hero_kicker}</p>
            <h1 className="font-heading text-5xl sm:text-6xl xl:text-7xl font-extrabold uppercase leading-[0.95] tracking-tight">
              {c.hero_title_pre}<span className="text-teal-400">{c.hero_title_highlight}</span>
            </h1>
            <p className="mt-6 text-xl text-teal-50/90 leading-relaxed max-w-xl">{c.hero_subtitle}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to={portalHref}><Button size="lg" data-testid="hero-portal-button" className="bg-teal-500 hover:bg-teal-400 text-teal-950 font-bold h-14 px-8 rounded-full text-base">{c.hero_cta_primary} <ArrowRight className="h-5 w-5 ml-1.5" /></Button></Link>
              <Link to="/servizi"><Button size="lg" variant="outline" className="h-14 px-8 rounded-full text-base border-white/40 bg-white/5 text-white hover:bg-white/15">{c.hero_cta_secondary}</Button></Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          {(c.features || []).map((f, i) => {
            const Icon = FEATURE_ICONS[i] || Sparkles;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="p-8 rounded-2xl bg-slate-900/60 border border-white/10 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow]">
                <div className="h-12 w-12 rounded-xl bg-teal-500/15 flex items-center justify-center mb-5"><Icon className="h-6 w-6 text-teal-600" /></div>
                <h3 className="font-heading text-xl font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-slate-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-sm font-bold uppercase tracking-widest text-rose-500">{c.services_kicker}</p>
            <h2 className="font-heading text-4xl font-bold text-white mt-2">{c.services_title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="group rounded-2xl overflow-hidden bg-slate-900/70 border border-white/10 shadow-sm">
              <div className="h-40 bg-white/90 flex items-center justify-center overflow-hidden">
                <img src={resolveImg(c.stampa_image)} alt={c.stampa_title} className="h-32 object-contain" />
              </div>
              <div className="p-8">
                <h3 className="font-heading text-2xl font-semibold text-white">{c.stampa_title}</h3>
                <p className="mt-3 text-slate-400 leading-relaxed">{c.stampa_desc}</p>
                <Link to="/servizi" className="inline-flex items-center gap-1.5 mt-5 text-teal-600 font-semibold hover:gap-3 transition-all">{c.card_link_label || "Approfondisci"} <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.12 }}
              className="group rounded-2xl overflow-hidden bg-slate-900/70 border border-white/10 shadow-sm">
              <div className="h-40 bg-white/90 flex items-center justify-center overflow-hidden">
                <img src={resolveImg(c.cad_image)} alt={c.cad_title} className="h-32 object-contain" />
              </div>
              <div className="p-8">
                <h3 className="font-heading text-2xl font-semibold text-white">{c.cad_title}</h3>
                <p className="mt-3 text-slate-400 leading-relaxed">{c.cad_desc}</p>
                <Link to="/servizi" className="inline-flex items-center gap-1.5 mt-5 text-teal-600 font-semibold hover:gap-3 transition-all">{c.card_link_label || "Approfondisci"} <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <Layers className="h-10 w-10 mx-auto mb-5 text-teal-200" />
          <h2 className="font-heading text-4xl font-bold">{c.cta_title}</h2>
          <p className="mt-4 text-teal-50/90 text-lg">{c.cta_subtitle}</p>
          <Link to={portalHref}><Button size="lg" className="mt-8 bg-white text-teal-700 hover:bg-teal-50 font-bold h-14 px-10 rounded-full">{c.cta_button}</Button></Link>
        </div>
      </section>
    </PublicLayout>
  );
}
